/**
 * آرشیو تصمیم‌ها.
 *
 * آرشیو از لاگ حسابرسی **همان ماشین** خوانده می‌شود و هیچ داده‌ای بیرون
 * نمی‌رود.
 *
 * دو نما دارد: فهرست تخت (هر تلاش یک ردیف) و زنجیره‌ها (تلاش‌های یک پرونده
 * با هم). زنجیره نمای مفیدتری است چون سیر اصلاح یک تصمیم را نشان می‌دهد، اما
 * فهرست تخت برای پیدا کردن یک مورد خاص سریع‌تر است.
 */

import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Archive, Layers, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { DataTable } from '@/components/ui/DataTable'
import { Tabs } from '@/components/ui/Tabs'
import {
  Badge,
  Banner,
  Card,
  EmptyState,
  Kpi,
  KpiRow,
  LoadingRows,
  SectionHeader,
  PageHeader,
} from '@/components/ui/primitives'
import { fa, faDateTime, orDash, truncate } from '@/lib/format'
import { VERDICT_TONE } from '@/lib/labels'
import { fetchArchive, type Scope } from '@/lib/api/endpoints'
import { queryKeys } from '@/lib/queries'

import { ArchiveDetail } from './ArchiveDetail'

import type { ArchiveEntry, ArchiveThread } from '@/lib/types'

interface ArchivePageProps {
  scope?: Scope
  /** زیرنویس سرصفحه. در کارگاه متفاوت است، چون مخاطبش ادمین است نه مشتری. */
  caption?: string
}

export const ArchivePage = ({ scope, caption }: ArchivePageProps) => {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<ArchiveEntry | null>(null)
  const [view, setView] = useState('threads')

  const { data, isPending, error } = useQuery({
    queryKey: queryKeys.archive(query, scope),
    queryFn: () => fetchArchive(query, scope),
  })

  const columns = useMemo<ColumnDef<ArchiveEntry, string>[]>(
    () => [
      {
        id: 'timestamp',
        header: 'زمان',
        accessorFn: (row) => row.timestamp,
        cell: (info) => (
          <span className="whitespace-nowrap text-xs text-ink-muted">
            {faDateTime(info.getValue())}
          </span>
        ),
      },
      {
        id: 'headline',
        header: 'موضوع',
        accessorFn: (row) => row.headline,
        cell: (info) => <span className="text-ink">{truncate(info.getValue(), 70)}</span>,
      },
      {
        id: 'verdict',
        header: 'حکم',
        accessorFn: (row) => row.verdict_label,
        cell: (info) => {
          const entry = info.row.original
          return (
            <Badge tone={entry.verdict === null ? 'neutral' : VERDICT_TONE[entry.verdict]}>
              {info.getValue()}
            </Badge>
          )
        },
      },
      {
        id: 'confidence',
        header: 'اطمینان',
        accessorFn: (row) => String(row.confidence ?? ''),
        cell: (info) => {
          const value = info.row.original.confidence
          return (
            <span className="tabular-nums text-ink-muted">
              {value === null ? '—' : fa(value)}
            </span>
          )
        },
      },
      {
        id: 'attempt',
        header: 'تلاش',
        accessorFn: (row) => String(row.attempt),
        cell: (info) => (
          <span className="tabular-nums text-ink-muted">{fa(info.row.original.attempt)}</span>
        ),
      },
    ],
    [],
  )

  if (error !== null) {
    return <Banner tone="danger">{error.message}</Banner>
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<Archive className="size-5.5" aria-hidden />}
        eyebrow="بایگانی"
        title="آرشیو تصمیم‌ها"
        caption={
          caption ??
          'مشاوره‌های گذشته‌ی همین نمونه. این داده روی همین ماشین می‌ماند.'
        }
      />

      {data !== undefined && (
        <KpiRow>
          <Kpi label="کل مشاوره‌ها" value={fa(data.stats.total)} />
          <Kpi label="کامل‌شده" value={fa(data.stats.completed)} />
          <Kpi label="بازسازی" value={fa(data.stats.refinements)} />
          <Kpi label="پرونده‌های مستقل" value={fa(data.stats.threads)} />
        </KpiRow>
      )}

      {data !== undefined && data.stats.unreadable_lines > 0 && (
        <Banner tone="warning">
          {fa(data.stats.unreadable_lines)} خط از لاگ خوانا نبود و در آمار نیامده است. بقیه‌ی
          آرشیو سالم است.
        </Banner>
      )}

      <Card>
        <SectionHeader
          title="فهرست"
          icon={<Archive className="size-4" />}
          actions={
            <div className="relative">
              <Search
                className="pointer-events-none absolute inset-y-0 start-3 my-auto size-4 text-ink-faint"
                aria-hidden
              />
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                }}
                placeholder="جست‌وجو در موضوع و متن"
                aria-label="جست‌وجو در آرشیو"
                className="h-10 w-full rounded-md border border-line-strong bg-surface-raised ps-10 pe-3 text-xs text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none sm:w-64"
              />
            </div>
          }
        />

        {isPending ? (
          <LoadingRows rows={5} />
        ) : data === undefined || data.stats.total === 0 ? (
          <EmptyState
            title="آرشیو خالی است"
            detail="پس از نخستین مشاوره، همه‌ی تلاش‌ها اینجا فهرست می‌شوند."
            icon={<Archive className="size-6" aria-hidden />}
          />
        ) : (
          <div className="p-4">
            <Tabs
              value={view}
              onValueChange={setView}
              items={[
                {
                  value: 'threads',
                  label: 'زنجیره‌ها',
                  icon: <Layers className="size-4" aria-hidden />,
                  content: (
                    <ThreadList
                      threads={data.threads}
                      onSelect={setSelected}
                      selectedId={selected?.consultation_id}
                    />
                  ),
                },
                {
                  value: 'flat',
                  label: 'همه‌ی تلاش‌ها',
                  icon: <Archive className="size-4" aria-hidden />,
                  content: (
                    <DataTable
                      columns={columns}
                      data={data.entries}
                      getRowId={(row) => row.consultation_id}
                      selectedId={selected?.consultation_id}
                      onRowClick={setSelected}
                      emptyMessage="نتیجه‌ای برای این جست‌وجو نیست."
                    />
                  ),
                },
              ]}
            />
          </div>
        )}
      </Card>

      {selected !== null && (
        <ArchiveDetail
          entry={selected}
          onClose={() => {
            setSelected(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * فهرست زنجیره‌ها.
 *
 * درون هر زنجیره، تلاش‌ها صعودی‌اند (تلاش ۱ اول) تا خواننده سیر اصلاح را از
 * ابتدا ببیند؛ اما فهرست زنجیره‌ها نزولی است تا آخرین کار بالا باشد.
 */
const ThreadList = ({
  threads,
  onSelect,
  selectedId,
}: {
  threads: ArchiveThread[]
  onSelect: (entry: ArchiveEntry) => void
  selectedId?: string
}) => {
  if (threads.length === 0) {
    return <p className="px-4 py-8 text-center text-sm text-ink-muted">زنجیره‌ای نیست.</p>
  }
  return (
    <ul className="space-y-2">
      {threads.map((thread) => (
        <li
          key={thread.root_consultation_id}
          className="overflow-hidden rounded-md border border-line bg-surface-raised shadow-raised transition-colors hover:border-accent-line"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line bg-surface-sunken/60 px-4 py-3">
            <p className="min-w-0 text-sm font-semibold text-ink">
              {truncate(thread.headline, 80)}
            </p>
            <span className="text-xs text-ink-muted">
              {fa(thread.attempts.length)} تلاش · {faDateTime(thread.latest.timestamp)}
            </span>
          </div>
          <ul className="divide-y divide-line">
            {thread.attempts.map((attempt) => (
              <li key={attempt.consultation_id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(attempt)
                  }}
                  className={
                    'flex w-full flex-wrap items-center gap-2.5 px-4 py-3 text-start text-xs transition-colors hover:bg-accent-soft/40' +
                    (attempt.consultation_id === selectedId
                      ? ' bg-accent-soft/70 font-medium'
                      : '')
                  }
                >
                  <span className="rounded-full bg-surface-sunken px-2 py-0.5 tabular-nums text-ink-muted ring-1 ring-inset ring-line">
                    تلاش {fa(attempt.attempt)}
                  </span>
                  <Badge
                    tone={attempt.verdict === null ? 'neutral' : VERDICT_TONE[attempt.verdict]}
                  >
                    {attempt.verdict_label}
                  </Badge>
                  {attempt.confidence !== null && (
                    <span className="tabular-nums text-ink-muted">
                      اطمینان {fa(attempt.confidence)}
                    </span>
                  )}
                  {attempt.revision_note !== '' && (
                    <span className="min-w-0 truncate text-ink-faint">
                      نکته: {orDash(attempt.revision_note)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}
